class FeaturedCollectionTabs {
  constructor(root) {
    this.root = root;
    this.activeHandle = '';
    this.hoverPaused = false;
    this.pauseUntil = 0;
    this.rafId = null;
    this.lastTime = 0;
    this.autoSpeed = 26;
    this.tabsDuplicated = false;
    this.originalTrackWidth = 0;
    this.isPointerDragging = false;
    this.isMouseDown = false;
    this.dragStartX = 0;
    this.dragStartScrollLeft = 0;
    this.dragDistance = 0;
  }

  init() {
    this.viewport = this.root.querySelector('[data-tab-viewport]');
    this.track = this.root.querySelector('[data-tab-track]');
    this.tabs = [...this.root.querySelectorAll('[data-tab]')];
    this.items = [...this.root.querySelectorAll('[data-item]')];
    this.seeAllButton = this.root.querySelector('[data-see-all-button]');

    if (!this.viewport || !this.track || this.tabs.length === 0) {
      return;
    }

    this.activeHandle = this.root.dataset.initialHandle || this.tabs[0].dataset.handle || '';
    this.bindEvents();
    this.setActiveCollection(this.activeHandle);
    this.setupCarousel();
  }

  bindEvents() {
    this.track.addEventListener('click', (event) => {
      const tab = event.target.closest('[data-tab]');
      if (!tab) return;
      if (this.isPointerDragging) return;
      this.setActiveCollection(tab.dataset.handle || '');
      this.pauseFor(3000);
    });

    this.viewport.addEventListener('mouseenter', () => {
      this.hoverPaused = true;
    });

    this.viewport.addEventListener('mouseleave', () => {
      this.hoverPaused = false;
    });

    ['touchstart', 'pointerdown', 'wheel'].forEach((eventName) => {
      this.viewport.addEventListener(
        eventName,
        () => {
          this.pauseFor(2000);
        },
        { passive: true }
      );
    });

    this.viewport.addEventListener('mousedown', (event) => {
      if (event.button !== 0) return;
      event.preventDefault();
      this.isMouseDown = true;
      this.isPointerDragging = false;
      this.dragDistance = 0;
      this.dragStartX = event.clientX;
      this.dragStartScrollLeft = this.viewport.scrollLeft;
      this.viewport.classList.add('is-dragging');
      this.pauseFor(2000);
    });

    this.viewport.addEventListener('mousemove', (event) => {
      if (!this.isMouseDown) return;
      const deltaX = event.clientX - this.dragStartX;
      this.dragDistance = Math.abs(deltaX);
      if (this.dragDistance > 4) {
        this.isPointerDragging = true;
      }
      this.viewport.scrollLeft = this.dragStartScrollLeft - deltaX;
    });

    window.addEventListener('mouseup', () => {
      if (!this.isMouseDown) return;
      this.isMouseDown = false;
      this.viewport.classList.remove('is-dragging');
      setTimeout(() => {
        this.isPointerDragging = false;
      }, 0);
    });

    window.addEventListener('resize', () => {
      this.applyVisibleItems();
      this.setupCarousel();
    });
  }

  setActiveCollection(handle) {
    if (!handle) return;
    this.activeHandle = handle;

    const matchingTabs = this.root.querySelectorAll(`[data-tab][data-handle="${handle}"]`);
    this.root.querySelectorAll('[data-tab]').forEach((tab) => tab.classList.remove('is-active'));
    matchingTabs.forEach((tab) => tab.classList.add('is-active'));

    const referenceTab = matchingTabs[0];
    if (referenceTab && this.seeAllButton) {
      const nextUrl = referenceTab.dataset.url || '#';
      this.seeAllButton.setAttribute('href', nextUrl);
    }

    this.applyVisibleItems();
  }

  getCurrentLimit() {
    const mobile = Number(this.root.dataset.limitMobile || 1);
    const tablet = Number(this.root.dataset.limitTablet || 1);
    const desktop = Number(this.root.dataset.limitDesktop || 1);

    if (window.matchMedia('(max-width: 749px)').matches) return mobile;
    if (window.matchMedia('(max-width: 989px)').matches) return tablet;
    return desktop;
  }

  applyVisibleItems() {
    const limit = this.getCurrentLimit();
    let shown = 0;
    this.items.forEach((item) => {
      const matchesTab = item.dataset.handle === this.activeHandle;
      const withinLimit = shown < limit;
      const visible = matchesTab && withinLimit;
      item.hidden = !visible;
      if (visible) shown += 1;
    });
  }

  pauseFor(ms) {
    const now = Date.now();
    this.pauseUntil = Math.max(this.pauseUntil, now + ms);
  }

  setupCarousel() {
    if (!this.viewport || !this.track) return;

    if (this.rafId) {
      window.cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

    if (this.tabsDuplicated) {
      this.track.querySelectorAll('[data-clone="true"]').forEach((node) => node.remove());
      this.tabsDuplicated = false;
    }

    const originals = [...this.track.querySelectorAll('[data-tab]')];
    if (originals.length < 2) return;

    this.originalTrackWidth = this.track.scrollWidth;

    const viewportWidth = this.viewport.clientWidth;
    const duplicatesNeeded = Math.ceil((viewportWidth * 3) / this.originalTrackWidth);

    for (let i = 0; i < Math.max(duplicatesNeeded, 2); i++) {
      originals.forEach((tab) => {
        const clone = tab.cloneNode(true);
        clone.dataset.clone = 'true';
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('tabindex', '-1');
        this.track.appendChild(clone);
      });
    }

    this.tabsDuplicated = true;
    this.viewport.scrollLeft = this.originalTrackWidth;
    this.lastTime = 0;
    this.startAutoScroll();
  }

  startAutoScroll() {
    const tick = (time) => {
      if (!this.lastTime) this.lastTime = time;
      const delta = (time - this.lastTime) / 1000;
      this.lastTime = time;

      const now = Date.now();
      const canScroll = !this.hoverPaused && now > this.pauseUntil;

      if (canScroll) {
        this.viewport.scrollLeft += this.autoSpeed * delta;
      }

      if (this.viewport.scrollLeft >= this.originalTrackWidth * 2) {
        this.viewport.scrollLeft -= this.originalTrackWidth;
      } else if (this.viewport.scrollLeft < this.originalTrackWidth * 0.5) {
        this.viewport.scrollLeft += this.originalTrackWidth;
      }

      this.rafId = window.requestAnimationFrame(tick);
    };

    this.rafId = window.requestAnimationFrame(tick);
  }
}

const initFeaturedCollectionTabs = () => {
  document.querySelectorAll('[data-featured-collection-tabs]').forEach((root) => {
    if (root.dataset.tabsInitialized === 'true') return;
    root.dataset.tabsInitialized = 'true';
    const instance = new FeaturedCollectionTabs(root);
    instance.init();
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initFeaturedCollectionTabs);
} else {
  initFeaturedCollectionTabs();
}

document.addEventListener('shopify:section:load', initFeaturedCollectionTabs);

const initAddToCartForms = () => {
  document.querySelectorAll('.featured-collection-tabs__atc-form').forEach((form) => {
    if (form.dataset.ajaxEnabled === 'true') return;
    form.dataset.ajaxEnabled = 'true';

    form.addEventListener('submit', async (event) => {
      event.preventDefault();

      const button = form.querySelector('button[type="submit"]');
      if (!button || button.disabled) return;

      button.disabled = true;
      const originalText = button.textContent;
      button.textContent = 'Adding...';

      try {
        const cartDrawer = document.querySelector('cart-drawer');
        const formData = new FormData(form);
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
          formData.append(
            'sections',
            cartDrawer.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          cartDrawer.setActiveElement(document.activeElement);
        }

        config.body = formData;

        const addResponse = await fetch(`${routes.cart_add_url}`, config);
        const addData = await addResponse.json();

        if (addData.status) {
          button.textContent = 'Error';
          setTimeout(() => {
            button.textContent = originalText;
            button.disabled = false;
          }, 2000);
          return;
        }

        if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
          cartDrawer.dataset.userTriggered = 'true';
          cartDrawer.renderContents(addData);
          button.textContent = 'Added!';
        } else {
          window.location.href = '/cart';
          return;
        }
        setTimeout(() => {
          button.textContent = originalText;
          button.disabled = false;
        }, 1500);
      } catch (error) {
        console.error('Add to cart error:', error);
        button.textContent = 'Error';
        setTimeout(() => {
          button.textContent = originalText;
          button.disabled = false;
        }, 2000);
      }
    });
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAddToCartForms);
} else {
  initAddToCartForms();
}

document.addEventListener('shopify:section:load', initAddToCartForms);
